/**
 * Shared DOM overlay styling for the leaderboard screens (SubmitScore +
 * Leaderboard). These two screens render as a centered DOM card over the canvas,
 * following the same precedent as passwordGate.ts: a full-screen overlay is far
 * more robust for a text input and a ranked table than positioning Phaser text,
 * and it avoids needing Phaser's DOM container support.
 */

/** Create the full-screen overlay container, appended to <body>. Caller removes
 *  it on scene shutdown. */
export function makeOverlay(): HTMLDivElement {
  injectStyleOnce();
  const overlay = document.createElement("div");
  overlay.className = "lb-overlay";
  document.body.appendChild(overlay);
  return overlay;
}

function injectStyleOnce(): void {
  if (document.getElementById("lb-style")) return;
  const style = document.createElement("style");
  style.id = "lb-style";
  style.textContent = `
    .lb-overlay {
      position: fixed; inset: 0; z-index: 9998;
      display: flex; align-items: center; justify-content: center;
      background: rgba(14, 15, 19, 0.82);
      font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
      padding: 16px; box-sizing: border-box;
    }
    .lb-card {
      display: flex; flex-direction: column; gap: 14px;
      width: min(560px, 92vw); max-height: 92vh; overflow: auto;
      padding: 24px 26px;
      background: #1a1d24; border: 1px solid #2c313c; border-radius: 10px;
      box-shadow: 0 0 40px rgba(0, 0, 0, 0.6);
    }
    .lb-title { color: #4fa3ff; font-size: 22px; font-weight: 700; letter-spacing: 0.5px; }
    .lb-sub { color: #9aa0ac; font-size: 13px; margin-top: -8px; }

    .lb-stats { display: grid; grid-template-columns: 1fr 1fr; gap: 8px 18px; }
    .lb-stat { display: flex; justify-content: space-between; gap: 12px;
      color: #e6e6e6; font-size: 15px; padding: 6px 0; border-bottom: 1px solid #23272f; }
    .lb-stat b { color: #ffb020; font-variant-numeric: tabular-nums; }

    .lb-input {
      background: #0e0f13; color: #e6e6e6;
      border: 1px solid #2c313c; border-radius: 6px;
      padding: 10px 12px; font: inherit; font-size: 15px; outline: none;
    }
    .lb-input:focus { border-color: #4fa3ff; }

    .lb-actions { display: flex; gap: 10px; }
    .lb-btn {
      flex: 1; background: #4fa3ff; color: #0e0f13;
      border: none; border-radius: 6px; padding: 11px 12px;
      font: inherit; font-size: 15px; font-weight: 700; cursor: pointer;
    }
    .lb-btn:hover:not(:disabled) { filter: brightness(1.08); }
    .lb-btn:disabled { opacity: 0.5; cursor: not-allowed; }
    .lb-btn-secondary { background: #2c313c; color: #e6e6e6; }

    .lb-error { color: #d14b4b; font-size: 13px; min-height: 1em; }

    .lb-table { width: 100%; border-collapse: collapse; font-size: 14px; }
    .lb-table th {
      text-align: left; color: #9aa0ac; font-weight: 600;
      padding: 6px 8px; border-bottom: 1px solid #2c313c;
    }
    .lb-table td { padding: 7px 8px; color: #e6e6e6; border-bottom: 1px solid #23272f;
      font-variant-numeric: tabular-nums; }
    .lb-table th.num, .lb-table td.num { text-align: right; }
    .lb-row-me td { background: #26344a; color: #ffffff; font-weight: 700; }
    .lb-row-me td:first-child { box-shadow: inset 3px 0 0 #4fa3ff; }
    .lb-muted { color: #9aa0ac; font-size: 13px; }
  `;
  document.head.appendChild(style);
}
